import { useMemo } from "react";
import { Icon, List, ListItem, Stack, Text } from "@bookingcom/bui-react";
import { CheckmarkFillIcon } from "@bookingcom/bui-assets-react/streamline";
import { t, useI18n } from "@bookingcom/lingojs-react";
import type { Airline } from "../../types";
import styles from "./AirlinePicker.module.css";

type AirlinePickerListProps = {
  airlines: Airline[];
  value?: string;
  onClick: (airline: Airline) => void;
};

export const AirlinePickerList = ({ airlines, value, onClick }: AirlinePickerListProps) => {
  const i18n = useI18n();

  const sortedAirlines = useMemo(() => {
    const selectedAirline = airlines.find(({ code }) => code === value);
    if (!selectedAirline) {
      return airlines;
    }

    return [selectedAirline, ...airlines.filter(({ code }) => code !== value)];
  }, [value, airlines]);

  // Helper function to format airline code with aliases
  const formatAirlineCode = (airline: Airline): string => {
    if (airline.aliases && airline.aliases.length > 0) {
      return [airline.code, ...airline.aliases].join(", ");
    }
    return airline.code;
  };

  if (!sortedAirlines.length) {
    return (
      <Stack mixin={{ paddingBlockStart: 3, paddingBlockEnd: 3, paddingInlineStart: 4, paddingInlineEnd: 4 }}>
        <Text variant="body_2" color="neutral_alt">
          {i18n.trans(t("flights_airline_portal_superuser_overview_report_dropdown_search_error"))}
        </Text>
      </Stack>
    );
  }

  return (
    <List
      className={styles.airlinePickerList}
      aria-label="Airline select menu"
      attributes={{ "data-testid": "AirlinePickerList" }}
    >
      {sortedAirlines.map((airline) => (
        <ListItem
          key={`kAirlinePickerListItem-${airline.code}`}
          spacing="small"
          onClick={() => onClick(airline)}
          className={styles.airlinePickerListItem}
          aria-selected={value === airline.code}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row">
              <Text color="neutral_alt">{formatAirlineCode(airline)}</Text>
              <Text>{airline.name}</Text>
            </Stack>
            {value === airline.code && <Icon svg={CheckmarkFillIcon} />}
          </Stack>
        </ListItem>
      ))}
    </List>
  );
};
